// frontend/src/testing/annuity-whale-dashboard.js
import React, { useState, useEffect } from 'react';
import { 
  MapPin, 
  Users, 
  CheckCircle, 
  AlertTriangle, 
  Calendar, 
  DollarSign, 
  BarChart, 
  PieChart, 
  ChevronDown, 
  Menu, 
  Search,
  Bell,
  User,
  FileText,
  TrendingUp,
  Globe,
  MessageSquare
} from 'lucide-react';

const AnnuityWhaleDashboard = () => {
  // State management
  const [selectedTab, setSelectedTab] = useState('dashboard');
  const [showReportModal, setShowReportModal] = useState(false);
  const [currentLead, setCurrentLead] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userData, setUserData] = useState({
    name: 'Jake Mossing',
    email: 'jake@whaleadvisors.com',
    avatar: null,
    claimedCounties: ['Maricopa County, AZ'],
    leads: [
      { id: 1, name: 'John D.', county: 'Maricopa County, AZ', value: '$250,000', status: 'New', date: '04/10/2025', phone: '(480) 555-1234', email: 'john.d@example.com' },
      { id: 2, name: 'Sarah M.', county: 'Maricopa County, AZ', value: '$175,000', status: 'Contacted', date: '04/09/2025', phone: '(480) 555-5678', email: 'sarah.m@example.com' },
      { id: 3, name: 'Robert T.', county: 'Maricopa County, AZ', value: '$400,000', status: 'Meeting Scheduled', date: '04/08/2025', phone: '(480) 555-8765', email: 'robert.t@example.com' },
      { id: 4, name: 'Patricia L.', county: 'Maricopa County, AZ', value: '$225,000', status: 'New', date: '04/07/2025', phone: '(480) 555-4321', email: 'patricia.l@example.com' },
      { id: 5, name: 'Michael W.', county: 'Maricopa County, AZ', value: '$310,000', status: 'Meeting Scheduled', date: '04/06/2025', phone: '(480) 555-7890', email: 'michael.w@example.com' },
      { id: 6, name: 'Jennifer K.', county: 'Maricopa County, AZ', value: '$185,000', status: 'Contacted', date: '04/05/2025', phone: '(480) 555-3456', email: 'jennifer.k@example.com' },
    ],
    notifications: [
      { id: 1, message: 'New lead assigned: John D.', time: '2 hours ago', read: false },
      { id: 2, message: 'Meeting reminder: Sarah M. at 2:00 PM', time: '5 hours ago', read: false },
      { id: 3, message: 'Lead status update: Robert T. is now "Meeting Scheduled"', time: '1 day ago', read: true },
    ],
    stats: {
      leadsThisMonth: 7,
      leadsConverted: 2,
      averageValue: '$217,500',
      conversionRate: '28%',
      totalContractValue: '$1,650,000',
      roi: '18.8x',
    }
  });
  
  const [availableCounties, setAvailableCounties] = useState([
    { id: 1, name: 'Franklin County, OH', status: 'available', leadsPerMonth: '3-5', price: '$350', population: '1.32M', avgIncome: '$64,500' },
    { id: 2, name: 'Denver County, CO', status: 'available', leadsPerMonth: '4-7', price: '$375', population: '706K', avgIncome: '$72,600' },
    { id: 3, name: 'Wake County, NC', status: 'available', leadsPerMonth: '2-4', price: '$325', population: '1.15M', avgIncome: '$78,400' },
    { id: 4, name: 'Maricopa County, AZ', status: 'claimed', leadsPerMonth: '5-9', price: '$400', population: '4.49M', avgIncome: '$68,700' },
    { id: 5, name: 'King County, WA', status: 'available', leadsPerMonth: '6-8', price: '$425', population: '2.27M', avgIncome: '$102,900' },
    { id: 6, name: 'Harris County, TX', status: 'available', leadsPerMonth: '7-10', price: '$385', population: '4.71M', avgIncome: '$69,200' },
    { id: 7, name: 'Orange County, CA', status: 'available', leadsPerMonth: '8-12', price: '$450', population: '3.17M', avgIncome: '$94,500' },
  ]);

  // Performance data for charts
  const [performanceData, setPerformanceData] = useState({
    leadConversion: [
      { name: 'Leads Received', value: 25, percentage: 100 },
      { name: 'Initial Contact Made', value: 20, percentage: 80 },
      { name: 'Meetings Scheduled', value: 15, percentage: 60 },
      { name: 'Contracts Signed', value: 7, percentage: 28 }
    ],
    monthlyLeads: [
      { month: 'Jan', leads: 5 },
      { month: 'Feb', leads: 7 },
      { month: 'Mar', leads: 6 },
      { month: 'Apr', leads: 7 },
      { month: 'May', leads: 9 },
      { month: 'Jun', leads: 8 }
    ],
    leadSources: [
      { source: 'Digital Ads', percentage: 45 },
      { source: 'Referrals', percentage: 25 },
      { source: 'Direct Search', percentage: 20 },
      { source: 'Other', percentage: 10 }
    ]
  });

  // Filter states for the leads tab
  const [leadFilters, setLeadFilters] = useState({
    status: 'all',
    dateRange: 'all',
    search: ''
  });

  // Handle tab selection
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    setMobileMenuOpen(false);
  };

  // Get color for status badges
  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-700';
      case 'Contacted': return 'bg-yellow-100 text-yellow-700';
      case 'Meeting Scheduled': return 'bg-green-100 text-green-700';
      case 'Closed': return 'bg-purple-100 text-purple-700';
      case 'Contract Signed': return 'bg-green-700 text-white';
      case 'Contract Declined': return 'bg-red-100 text-red-700';
      case 'No Response': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Filter leads based on leadFilters
  const filteredLeads = userData.leads.filter(lead => {
    // Filter by status
    if (leadFilters.status !== 'all' && lead.status !== leadFilters.status) {
      return false;
    }
    
    // Filter by search term
    if (leadFilters.search && !lead.name.toLowerCase().includes(leadFilters.search.toLowerCase()) && 
        !lead.county.toLowerCase().includes(leadFilters.search.toLowerCase())) {
      return false;
    }
    
    // For simplicity, we'll skip date range filtering in this example
    return true;
  });

  // Handle claiming a new county
  const handleClaimCounty = (countyId) => {
    const county = availableCounties.find(county => county.id === countyId);
    if (county) {
      // Update the county status
      const updatedCounties = availableCounties.map(c => 
        c.id === countyId ? { ...c, status: 'claimed' } : c
      );
      setAvailableCounties(updatedCounties);
      
      // Add to user's claimed counties
      setUserData({
        ...userData,
        claimedCounties: [...userData.claimedCounties, county.name]
      });
      
      // Show success message (in a real app, this would be a modal or toast)
      alert(`Successfully claimed ${county.name}! You'll start receiving leads in this territory.`);
    }
  };

  // Handle lead status update
  const handleLeadStatusUpdate = (leadId, newStatus, contractAmount = null) => {
    const updatedLeads = userData.leads.map(lead => 
      lead.id === leadId ? { ...lead, status: newStatus } : lead
    );
    
    setUserData({
      ...userData,
      leads: updatedLeads
    });
    
    setShowReportModal(false);
    
    // Show success message
    alert(`Lead status updated successfully to ${newStatus}`);
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header */}
      <header className="bg-blue-900 text-white shadow-md">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="flex items-center">
              <img src="/api/placeholder/48/48" className="mr-3 h-8" alt="Annuity Whale Logo" />
              <div className="text-xl font-bold">Annuity Whale</div>
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-4">
            <button className="bg-blue-700 hover:bg-blue-800 px-4 py-2 rounded-md text-sm font-medium">
              Claim New County
            </button>
            <div className="relative">
              <Bell className="h-5 w-5 cursor-pointer text-blue-200 hover:text-white" />
              {userData.notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 flex items-center justify-center text-xs">
                  {userData.notifications.filter(n => !n.read).length}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-blue-700 flex items-center justify-center">
                {userData.avatar ? (
                  <img src={userData.avatar} alt="Avatar" className="w-8 h-8 rounded-full" />
                ) : (
                  <span className="text-sm font-medium">{userData.name.charAt(0)}</span>
                )}
              </div>
              <span className="text-sm font-medium">Hello, {userData.name}</span>
            </div>
          </div>
          <button 
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Menu size={24} />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-blue-800 text-white">
          <div className="container mx-auto px-4 py-2">
            <div className="flex flex-col space-y-3">
              <div className="flex items-center space-x-2 py-2 border-b border-blue-700">
                <div className="w-8 h-8 rounded-full bg-blue-700 flex items-center justify-center">
                  {userData.name.charAt(0)}
                </div>
                <div>
                  <div className="text-sm font-medium">{userData.name}</div>
                  <div className="text-xs text-blue-300">{userData.email}</div>
                </div>
              </div>
              <button 
                onClick={() => handleTabChange('dashboard')}
                className="text-left py-2 px-1 text-sm hover:bg-blue-700 rounded"
              >
                Dashboard
              </button>
              <button 
                onClick={() => handleTabChange('leads')}
                className="text-left py-2 px-1 text-sm hover:bg-blue-700 rounded"
              >
                My Leads
              </button>
              <button 
                onClick={() => handleTabChange('territories')}
                className="text-left py-2 px-1 text-sm hover:bg-blue-700 rounded"
              >
                Territory Map
              </button>
              <button 
                onClick={() => handleTabChange('marketplace')}
                className="text-left py-2 px-1 text-sm hover:bg-blue-700 rounded"
              >
                County Marketplace
              </button>
              <button 
                onClick={() => handleTabChange('performance')}
                className="text-left py-2 px-1 text-sm hover:bg-blue-700 rounded"
              >
                Report Performance
              </button>
              <button className="bg-blue-600 hover:bg-blue-700 w-full py-2 rounded-md text-sm font-medium mt-3">
                Claim New County
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="bg-white shadow-sm hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8">
            <button
              onClick={() => handleTabChange('dashboard')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'dashboard'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => handleTabChange('leads')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'leads'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              My Leads
            </button>
            <button
              onClick={() => handleTabChange('territories')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'territories'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Territory Map
            </button>
            <button
              onClick={() => handleTabChange('marketplace')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'marketplace'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              County Marketplace
            </button>
            <button
              onClick={() => handleTabChange('performance')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'performance'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Report Performance
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Dashboard */}
        {selectedTab === 'dashboard' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Advisor Dashboard</h1>
              <div className="flex space-x-2">
                <button className="hidden md:block bg-white border border-gray-300 text-gray-700 px-3 py-1.5 rounded-md text-sm font-medium hover:bg-gray-50">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  Apr 11, 2025
                </button>
              </div>
            </div>
            
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                    <Users size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Leads This Month</p>
                    <p className="text-2xl font-semibold text-gray-900">{userData.stats.leadsThisMonth}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-green-100 text-green-600">
                    <CheckCircle size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Converted Leads</p>
                    <p className="text-2xl font-semibold text-gray-900">{userData.stats.leadsConverted}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
                    <DollarSign size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Avg. Lead Value</p>
                    <p className="text-2xl font-semibold text-gray-900">{userData.stats.averageValue}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                    <TrendingUp size={20} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Conversion Rate</p>
                    <p className="text-2xl font-semibold text-gray-900">{userData.stats.conversionRate}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Your Territories */}
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">Your Claimed Territories</h2>
              </div>
              <div className="p-6">
                {userData.claimedCounties.length > 0 ? (
                  <div className="space-y-4">
                    {userData.claimedCounties.map((county, index) => (
                      <div key={index} className="flex flex-col md:flex-row md:items-center md:justify-between p-4 bg-blue-50 rounded-lg">
                        <div className="flex items-center mb-3 md:mb-0">
                          <MapPin className="text-blue-600 mr-2" size={20} />
                          <span className="font-medium">{county}</span>
                        </div>
                        <div className="flex space-x-2">
                          <button className="text-sm bg-white text-blue-600 border border-blue-600 px-3 py-1 rounded">
                            View Stats
                          </button>
                          <button 
                            onClick={() => handleTabChange('leads')}
                            className="text-sm bg-blue-600 text-white px-3 py-1 rounded"
                          >
                            Manage Leads
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-gray-500">You haven't claimed any territories yet.</p>
                    <button 
                      onClick={() => handleTabChange('marketplace')}
                      className="mt-2 bg-blue-600 text-white px-4 py-2 rounded"
                    >
                      Claim Your First County
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900">Recent Lead Activity</h2>
                <div className="flex items-center">
                  <select className="text-sm border-gray-300 rounded-md shadow-sm">
                    <option>Last 7 Days</option>
                    <option>Last 30 Days</option>
                    <option>All Time</option>
                  </select>
                </div>
              </div>
              <div className="divide-y divide-gray-200">
                {userData.leads.slice(0, 3).map((lead) => (
                  <div key={lead.id} className="p-6 flex items-center">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {lead.name} - {lead.county}
                      </p>
                      <p className="text-sm text-gray-500">
                        Annuity Value: {lead.value} · {lead.date}
                      </p>
                    </div>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                        {lead.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-6 py-4 border-t border-gray-200">
                <button 
                  onClick={() => handleTabChange('leads')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  View all leads →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Leads */}
        {selectedTab === 'leads' && (
  <div>
    <div className="sm:flex sm:items-center sm:justify-between mb-6">
      <h1 className="text-2xl font-bold text-gray-900">My Leads</h1>
      <div className="mt-3 sm:mt-0 sm:ml-4 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
        <button className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 flex items-center">
          <FileText className="h-4 w-4 mr-1" />
          Export Leads
        </button>
      </div>
    </div>
    
    <div className="bg-white shadow rounded-lg overflow-hidden">
      {/* Search and filters */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search leads..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md"
              value={leadFilters.search}
              onChange={(e) => setLeadFilters({...leadFilters, search: e.target.value})}
            />
          </div>
          <div className="flex space-x-2">
            <select 
              className="border border-gray-300 rounded-md px-3 py-2"
              value={leadFilters.status}
              onChange={(e) => setLeadFilters({...leadFilters, status: e.target.value})}
            >
              <option value="all">All Statuses</option>
              <option value="New">New</option>
              <option value="Contacted">Contacted</option>
              <option value="Meeting Scheduled">Meeting Scheduled</option>
              <option value="Closed">Closed</option>
            </select>
            <select 
              className="border border-gray-300 rounded-md px-3 py-2"
              value={leadFilters.dateRange}
              onChange={(e) => setLeadFilters({...leadFilters, dateRange: e.target.value})}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Data Table */}
      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lead
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      County
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredLeads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                            <User size={16} className="text-gray-500" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                            <div className="text-sm text-gray-500">{lead.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{lead.county}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{lead.value}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{lead.date}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2 justify-end">
                          <button className="text-blue-600 hover:text-blue-900">View</button>
                          <button 
                            onClick={() => {
                              setCurrentLead(lead);
                              setShowReportModal(true);
                            }}
                            className="text-green-600 hover:text-green-900"
                          >
                            Report Outcome
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

        {/* Territory Map Placeholder */}
        {selectedTab === 'territories' && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Territory Map</h1>
            <div className="bg-white shadow rounded-lg p-6">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <div className="text-gray-500">
                  <Globe className="h-12 w-12 mx-auto text-gray-400" />
                  <p className="text-lg font-medium mb-2 mt-4">Interactive Map Coming Soon</p>
                  <p className="max-w-md mx-auto">The interactive territory map will allow you to view available counties and your claimed territories.</p>
                  <div className="mt-6 bg-blue-50 p-4 rounded-lg inline-block mx-auto">
                    <p className="font-medium text-blue-700">Your Claimed County:</p>
                    <p className="text-blue-800 font-bold mt-1">Maricopa County, AZ</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* County Marketplace */}
        {selectedTab === 'marketplace' && (
          <div>
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-900">County Marketplace</h1>
              <div className="mt-3 sm:mt-0 sm:ml-4">
                <div className="flex space-x-2">
                  <input 
                    type="text" 
                    placeholder="Search counties..." 
                    className="border border-gray-300 rounded-md px-4 py-2 text-sm" 
                  />
                  <button className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700">
                    Search
                  </button>
                </div>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900">Available Counties</h3>
                <p className="text-sm text-gray-500 mt-1">Claim exclusive rights to these territories and start receiving leads.</p>
              </div>
              
              <div className="divide-y divide-gray-200">
                {availableCounties
                  .filter(county => county.status === 'available')
                  .map((county) => (
                  <div key={county.id} className="p-6 flex flex-col md:flex-row md:items-center md:justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">{county.name}</h3>
                      <div className="mt-1 text-sm text-gray-500">
                        <div className="flex flex-col md:flex-row md:space-x-6">
                          <p>Population: <span className="font-medium">{county.population}</span></p>
                          <p>Avg Income: <span className="font-medium">{county.avgIncome}</span></p>
                          <p>Expected leads: <span className="font-medium">{county.leadsPerMonth} per month</span></p>
                        </div>
                      </div>
                    </div>
                    <div className="text-right mt-4 md:mt-0">
                      <p className="text-xl font-bold text-blue-700">${county.price}<span className="text-sm font-normal text-gray-500">/lead</span></p>
                      <button 
                        onClick={() => handleClaimCounty(county.id)}
                        className="mt-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md text-sm font-medium"
                      >
                        Claim County
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900">Claimed Counties</h3>
                <p className="text-sm text-gray-500 mt-1">These territories are already claimed by other advisors.</p>
              </div>
              
              <div className="divide-y divide-gray-200">
                {availableCounties
                  .filter(county => county.status === 'claimed')
                  .map((county) => (
                  <div key={county.id} className="p-6 flex flex-col md:flex-row md:items-center md:justify-between bg-gray-50">
                    <div>
                      <h3 className="text-lg font-medium text-gray-700">{county.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Expected leads: <span className="font-medium">{county.leadsPerMonth} per month</span>
                      </p>
                    </div>
                    <div className="text-right mt-4 md:mt-0">
                      <p className="text-xl font-bold text-gray-400">${county.price}<span className="text-sm font-normal text-gray-500">/lead</span></p>
                      <button className="mt-2 bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium cursor-not-allowed">
                        Already Claimed
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Performance Reporting */}
        {selectedTab === 'performance' && (
          <div>
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Performance Reporting</h1>
              <div className="mt-3 sm:mt-0 sm:ml-4">
                <button className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700">
                  Download Performance Report
                </button>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900">Performance Dashboard</h3>
                <p className="text-sm text-gray-500 mt-1">Track your lead conversion metrics and help us improve our lead quality.</p>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-blue-50 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-blue-800 mb-4">Lead Conversion Stats</h4>
                    <div className="space-y-4">
                      {performanceData.leadConversion.map((item, index) => (
                        <div key={index}>
                          <p className="text-sm text-gray-500">{item.name}</p>
                          <div className="flex items-center mt-1">
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                              <div 
                                className="bg-blue-600 h-2.5 rounded-full" 
                                style={{ width: `${item.percentage}%` }}
                              ></div>
                            </div>
                            <span className="ml-2 text-sm font-medium">{item.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-green-50 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-green-800 mb-4">Total Contract Value</h4>
                    <div className="flex items-center justify-center h-40">
                      <div className="text-center">
                        <p className="text-4xl font-bold text-green-600">{userData.stats.totalContractValue}</p>
                        <p className="text-sm text-gray-500 mt-2">Total value of contracts from Annuity Whale leads</p>
                        <p className="text-xl font-semibold text-green-600 mt-4">ROI: {userData.stats.roi}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-medium text-gray-900">Report Lead Outcomes</h3>
                <p className="text-sm text-gray-500 mt-1">Help us improve our lead quality by reporting the outcome of your leads.</p>
              </div>
              
              <div className="p-6">
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="h-5 w-5 text-yellow-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        Reporting lead outcomes helps us improve lead quality and increases your ROI over time. 
                        We use this data to optimize our targeting and provide you with better leads.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lead</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report Outcome</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {userData.leads.map((lead) => (
                        <tr key={lead.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                            <div className="text-sm text-gray-500">{lead.county}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{lead.date}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                              {lead.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button 
                              onClick={() => {
                                setCurrentLead(lead);
                                setShowReportModal(true);
                              }}
                              className="inline-flex items-center px-3 py-1.5 border border-green-600 text-sm font-medium rounded-md text-green-600 bg-white hover:bg-green-50"
                            >
                              Update Status
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Lead Outcome Reporting Modal */}
      {showReportModal && currentLead && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Report Lead Outcome</h3>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  &times;
                </button>
              </div>
            </div>
            
            <div className="px-6 py-4">
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-1">Lead Information</p>
                <p className="text-sm text-gray-600">{currentLead.name} - {currentLead.county}</p>
                <p className="text-sm text-gray-600">Annuity Value: {currentLead.value}</p>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Status
                </label>
                <div className="relative">
                  <select
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    defaultValue={currentLead.status}
                  >
                    <option>New</option>
                    <option>Contacted</option>
                    <option>Meeting Scheduled</option>
                    <option>Meeting Held</option>
                    <option>Proposal Sent</option>
                    <option>Contract Signed</option>
                    <option>Contract Declined</option>
                    <option>No Response</option>
                    <option>Bad Lead</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contract Amount (if signed)
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">$</span>
                  </div>
                  <input
                    type="text"
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                    placeholder="0.00"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lead Quality Rating
                </label>
                <div className="flex items-center space-x-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      className="w-8 h-8 rounded-full bg-gray-200 hover:bg-yellow-400 flex items-center justify-center text-sm font-medium"
                    >
                      {rating}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">1 = Poor quality, 5 = Excellent quality</p>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Comments
                </label>
                <textarea
                  rows={3}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="Any additional feedback on this lead..."
                ></textarea>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowReportModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleLeadStatusUpdate(currentLead.id, 'Contract Signed');
                }}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnuityWhaleDashboard;